/**
 * Checks if the code is running in a browser environment.
 * @returns {boolean} True if the code is running in a browser, false otherwise.
 */
const isBrowser = () => typeof window !== 'undefined';

/**
 * Ensures that the global `tp` queue exists in the browser environment.
 * If the code is running in a browser and the `tp` property is not defined on the `window` object,
 * it initializes `window.tp` as an empty array.
 */
function ensureQueue() {
  if (isBrowser() && !window.tp) window.tp = [];
}

/**
 * Executes a command using the `tp` function if it exists in the browser's global `window` object.
 * If the `tp` function is not available, the command is queued for later execution.
 * @param {any} cmd - The command to be executed or queued.
 */
function tpPush(cmd) {
  if (!isBrowser()) return;

  if (typeof window.tp === 'function') {
    window.tp(cmd);
  } else {
    if (!Array.isArray(window.tp)) {
      const current = window.tp || {};
      window.tp = [];
      Object.assign(window.tp, current);
    }
    window.tp.push(cmd);
  }
}

/**
 * Sets the user reference token for the Piano service.
 * @param {string} tinypassToken - The user reference token to be set.
 */
function setUserReference(tinypassToken) {
  if (!isBrowser()) return;

  window.tp = window.tp || [];
  if (tinypassToken) {
    window.tp.userRef = tinypassToken;
  } else {
    delete window.tp.userRef;
  }
  tpPush(['setUserRef', tinypassToken]);
}

/**
 * Sets a custom variable in Piano.
 * @param {string} key - The variable name.
 * @param {string} value - The variable value.
 */
function setVariable(key, value) {
  if (!isBrowser()) return;

  tpPush(['setCustomVariable', key, value]);
}

let last = 0;
/**
 * Reloads the gate experience if the required conditions are met.
 * Ensures the function is executed in a browser environment and throttles
 * execution to prevent it from running more than once per second.
 * @throws Will catch and suppress any errors thrown by `window.tp.experience.execute`.
 */
function reloadGate() {
  if (!isBrowser() || !window.tp?.experience?.execute) return;
  const now = Date.now();
  if (now - last < 1000) return;
  last = now;
  window.tp.experience.execute();
}

/**
 * Sets the user reference and reloads the Piano experience.
 * This ensures Piano recognizes the auth state change.
 * @param {string} tinypassToken - The user reference token to be set.
 */
function setUserAndReload(tinypassToken) {
  if (!isBrowser()) return;

  // Set userRef
  setUserReference(tinypassToken);

  // Set custom variables
  setVariable('updatedEvents', 'true');

  if (window.tp?.experience?.execute) {
    window.tp.experience.execute();
  } else {
    window.tp.push(() => {
      window.tp.experience.execute();
    });
  }
}

/**
 * Starts the checkout process with proper params
 * @param {object} params - The checkout parameters
 */
function startCheckout(params) {
  if (!isBrowser()) return;
  if (window.tp?.offer?.startCheckout) {
    window.tp.offer.startCheckout(params);
  } else {
    ensureQueue();
    window.tp.push(() => window.tp.offer.startCheckout(params));
  }
}

/**
 * Waits for the SDK to be loaded and initialized on the client side.
 * Returns false immediately if running on the server side (no window object).
 * If the SDK is already loaded, resolves immediately with true.
 * Otherwise, pushes an initialization callback to the tp queue that logs
 * user reference and validation status when the SDK is ready.
 *
 * @returns {Promise<boolean>} A promise that resolves to true when SDK is loaded/initialized,
 * or false if running server-side
 */
function waitForSDK() {
  if (typeof window === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    window.tp = window.tp || [];

    if (typeof window.tp.experience !== 'undefined') {
      resolve(true);
      return;
    }
    // push the init call
    window.tp.push(['init', function initCallback() {
    }]);
  });
}

// Store for current offer params to avoid global pollution
let currentOfferParams = null;

/**
 * Sets the current offer parameters
 * @param {object} params - The offer parameters from Piano
 */
function setCurrentOfferParams(params) {
  currentOfferParams = params;
}

/**
 * Gets the current offer parameters
 * @returns {object|null} The stored offer parameters
 */
function getCurrentOfferParams() {
  return currentOfferParams;
}

/**
 * Clears the current offer parameters
 */
function clearCurrentOfferParams() {
  currentOfferParams = null;
}

/**
 * Executes a command by pushing it to the tpPush queue along with a resolve callback.
 *
 * @param {Array} command - The command array to be executed
 * @returns {Promise} A promise that resolves when the command execution completes
 */
function executeCommand(command) {
  return new Promise((resolve, reject) => {
    try {
      tpPush([...command, resolve]);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Fires a Piano “customEvent” after SDK is ready
 * @param {object} payload – { eventName: 'bedrock-auth-toggle', isShown: true }
 */
function sendCustomEvent(payload) {
  if (!isBrowser()) return;

  if (typeof window.tp === 'function') {
    if (window.tp.offer?.customEvent) {
      window.tp.offer.customEvent(payload);
    } else {
      window.tp([
        'addHandler',
        'ready',
        function onReady() {
          window.tp.offer?.customEvent?.(payload);
          window.tp(['removeHandler', 'ready', onReady]);
        },
      ]);
    }
    return;
  }

  ensureQueue();
  window.tp.push([
    'addHandler',
    'ready',
    function onReady() {
      window.tp.offer?.customEvent?.(payload);
      window.tp(['removeHandler', 'ready', onReady]);
    },
  ]);
}

// eslint-disable-next-line import/no-default-export
export default {
  tpPush,
  setUserReference,
  reloadGate,
  startCheckout,
  setCurrentOfferParams,
  getCurrentOfferParams,
  clearCurrentOfferParams,
  setVariable,
  setUserAndReload,
  waitForSDK,
  executeCommand,
  sendCustomEvent,
};
